import { FormattedMessage, FormattedNumber, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import TimedOrCategorySearchCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import { itemSortOrders } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCuratedCollectionLabel, getCuratedCollectionTotalFound } from '@/redux/modules/curatedCollection';
import { changePaginationSortOrder, getPaginationSortOrder } from '@/redux/modules/pagination';
import { parseQueryParams } from '@/utils/queryParams';
import { toQueryString } from '@/utils/urls';

import CuratedCollectionPageHeaderMeta from './components/CuratedCollectionPageHeaderMeta';
import useGetSortedCollectionItemIds from './hooks/useGetSortedCollectionItemIds';

type CollectionRouteParams = {
    collectionId: string;
    collectionName: string;
};

const CURATED_COLLECTION_PAGE = 'curated-collection-page';

const CuratedCollectionPage = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const { collectionId, collectionName } = useParams<CollectionRouteParams>();
    const queryParams = parseQueryParams(search);
    const collectionLabel = useAppSelector(getCuratedCollectionLabel);
    const count = useAppSelector(getCuratedCollectionTotalFound);
    const paginationSortOrder = useAppSelector((state) => getPaginationSortOrder(state, 'curated-collection-page'));
    const sort = queryParams.sort || paginationSortOrder || itemSortOrders.TIME_SOONEST_FIRST;
    const itemIds = useGetSortedCollectionItemIds(sort);

    const firstItemId = itemIds?.[0] || 0;

    const getSortByOption = useCallback(
        (option) => {
            const label = formatMessage({ id: option.toLowerCase() });
            return { label, value: option };
        },
        [formatMessage]
    );

    const sortByOptions = Object.keys(itemSortOrders).map(getSortByOption);

    const sortSelected = useCallback(
        ({ value }: OptionType) => {
            dispatch(changePaginationSortOrder(CURATED_COLLECTION_PAGE, value));
            navigate({
                pathname,
                search: toQueryString({
                    ...queryParams,
                    sort: value,
                }),
            });
        },
        [dispatch, pathname, navigate, queryParams]
    );

    return (
        <StyledCuratedCollection>
            <CuratedCollectionPageHeaderMeta
                collectionId={Number(collectionId)}
                collectionLabel={collectionLabel}
                collectionName={collectionName}
                itemId={firstItemId}
            />
            <StyledTitleCount>
                <PageTitle title={collectionLabel} />
                <StyledResultsCount color="secondary">
                    <FormattedMessage
                        id="search_item_count_items"
                        key="curated-collection-page-search-item-count-items"
                        values={{
                            count: (
                                <FormattedNumber
                                    key="curated-collection-page-results-count"
                                    style="decimal"
                                    value={count}
                                />
                            ),
                        }}
                    />
                </StyledResultsCount>
            </StyledTitleCount>
            <StyledSortBar>
                <StyledSortBySelect
                    isInsideLabel
                    labelText={
                        <>
                            <FormattedMessage
                                id="sort"
                                key="curated-collection-page-sort"
                            />
                            :
                        </>
                    }
                    onChange={(option) => sortSelected(option)}
                    options={sortByOptions}
                    small
                    value={getSortByOption(sort)}
                />
            </StyledSortBar>
            <StyledFluidItemGrid
                itemCardOption={TimedOrCategorySearchCard}
                itemIds={itemIds}
            />
        </StyledCuratedCollection>
    );
};

export default CuratedCollectionPage;

const StyledCuratedCollection = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    min-height: 400px;
    padding: 0 20px 64px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 20px auto;
`;

const StyledFluidItemGrid = styled(FluidItemGrid)`
    margin-bottom: 16px;
`;

const StyledTitleCount = styled.div`
    @media (max-width: 416px) {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    display: flex;
    flex-flow: row wrap;
    align-items: baseline;
`;

const StyledResultsCount = styled(BodySecondary)`
    padding-right: 6px;
    margin-left: 8px;
`;

const StyledSortBySelect = styled<typeof Select>(Select)`
    max-width: 350px;
    min-width: 300px;
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        float: right;
    }
`;

const StyledSortBar = styled.div`
    margin-bottom: 24px;
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-bottom: 32px;
    }
`;
